/**
 * ABC News RSS Feed Provider
 * 
 * Fetches articles from ABC News (Australian Broadcasting Corporation) RSS feeds.
 * Free public service, no API keys required.
 * Focuses on Australian emergency, breaking, and local news coverage.
 * 
 * RSS Feeds:
 * - Breaking News: https://www.abc.net.au/news/feed/51120/rss.xml
 * - Emergency: https://www.abc.net.au/news/feed/13661068/rss.xml
 * - State-specific feeds for targeted coverage
 */

import relevanceEngine from '../relevanceEngine.js';

class ABCNewsFeedProvider {
  constructor() {
    this.name = 'ABC News Australia';
    this.baseUrl = 'https://www.abc.net.au/news/feed';
    this.isEnabled = true;
    this.localProxyAvailable = false; // Will be set after connectivity test
    this.lastProxyTest = 0; // Rate limiting for proxy tests
    
    // RSS proxy endpoint from environment variable with fallback
    const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
    this.localProxy = `${rssProxyUrl}?url=`;
    
    console.log('📡 ABC News RSS Provider initialized with RSS Proxy:', rssProxyUrl);
    
    // ABC News specific RSS endpoints - CLEANED UP: Removed broken state feeds
    this.endpoints = {
      breaking: 'https://www.abc.net.au/news/feed/51120/rss.xml',
      emergency: 'https://www.abc.net.au/news/feed/13661068/rss.xml', 
      national: 'https://www.abc.net.au/news/feed/45910/rss.xml',
      nsw: 'https://www.abc.net.au/news/feed/45910/rss.xml' // Use national feed as NSW-specific feed is broken
      // REMOVED: All state feeds (VIC, QLD, WA, SA, TAS, ACT, NT) - they return 500 errors
    };
  }

  /**
   * Test local proxy connectivity with rate limiting
   */
  async testLocalProxyConnectivity() {
    // Rate limiting: Only test once per minute
    const now = Date.now();
    if (this.lastProxyTest && (now - this.lastProxyTest) < 60000) {
      console.log('⏱️ ABC News proxy test skipped - rate limited (last test was less than 1 minute ago)');
      return;
    }
    
    this.lastProxyTest = now;
    console.log('🔬 Testing ABC News proxy connectivity...');
    
    try {
      const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
      const response = await fetch(rssProxyUrl, {
        timeout: 5000
      });
      
      if (response.ok) {
        console.log('✅ ABC News proxy is accessible');
        this.localProxyAvailable = true;
      } else {
        console.warn('⚠️ ABC News proxy health check failed:', response.status);
        this.localProxyAvailable = false;
      }
    } catch (error) {
      console.warn('⚠️ ABC News proxy connection failed:', error.message);
      this.localProxyAvailable = false;
    }
  }

  /**
   * Fetch articles from ABC News RSS feeds
   */
  async fetchArticles(options = {}) {
    console.log(`🚨 ABC News Provider: Fetching articles - ${new Date().toISOString()}`);
    console.log('🔍 ABC News - Fetching with options:', options);
    
    let allArticles = [];
    let searchedSources = []; // Track what sources we actually searched
    
    try {
      const feeds = this.buildFeedUrls(options);
      console.log(`📡 ABC News: Attempting to fetch from ${feeds.length} feeds using RSS PROXY...`);

      for (const feed of feeds) {
        // Track this source as searched
        searchedSources.push({
          name: feed.name,
          type: feed.type,
          status: 'attempted'
        });

        try {
          console.log(`📰 ABC News: Fetching from: ${feed.name}`);
          const articles = await this.fetchFromFeedWithProxy(feed.url);
          
          if (articles && articles.length > 0) {
            // Add feed metadata to articles
            const enrichedArticles = articles.map(article => ({
              ...article,
              feedSource: feed.name,
              feedType: feed.type,
              feedLocation: feed.location || null,
              provider: 'ABC News Australia'
            }));
            
            allArticles.push(...enrichedArticles);
            console.log(`✅ ABC News ${feed.name}: ${articles.length} articles`);
            
            // 🚨 ABC DIAGNOSTIC: Log first few article titles for verification
            if (articles.length > 0) {
              console.log(`🚨 ABC DIAGNOSTIC: Sample articles from ${feed.name}:`);
              articles.slice(0, 3).forEach((article, index) => {
                console.log(`   ${index + 1}. "${article.title}"`);
                console.log(`      📅 Published: ${article.publishedAt}`);
                console.log(`      📍 Location: ${article.location || 'none detected'}`);
                console.log(`      📝 Content: "${article.content?.substring(0, 100)}..."`);
              });
            }
            
            // Update search status
            const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
            if (sourceIndex >= 0) {
              searchedSources[sourceIndex].status = 'success';
              searchedSources[sourceIndex].articleCount = articles.length;
            }
          } else {
            console.log(`⚠️ ABC News ${feed.name}: No articles found`);
            // Update search status
            const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
            if (sourceIndex >= 0) {
              searchedSources[sourceIndex].status = 'no_results';
            }
          }
          
          // Rate limiting: Small delay between feed requests
          await this.delay(500);
          
        } catch (feedError) {
          console.warn(`⚠️ ABC News feed error for ${feed.name}:`, feedError.message);
          // Update search status
          const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
          if (sourceIndex >= 0) {
            searchedSources[sourceIndex].status = 'error';
            searchedSources[sourceIndex].error = feedError.message;
          }
        }
      }

      // Store search metadata for debugging
      this.lastSearchMetadata = {
        timestamp: new Date(),
        searchedSources: searchedSources,
        totalArticles: allArticles.length,
        feedsAttempted: feeds.length,
        feedsSuccessful: searchedSources.filter(s => s.status === 'success').length
      };

      console.log(`✅ ABC News: Retrieved ${allArticles.length} total articles from ${feeds.length} feeds`);
      return allArticles;

    } catch (error) {
      console.error('❌ ABC News Provider - Critical error:', error);
      
      // Store error metadata
      this.lastSearchMetadata = {
        timestamp: new Date(),
        searchedSources: searchedSources,
        totalArticles: 0,
        error: error.message,
        feedsAttempted: 0,
        feedsSuccessful: 0
      };
      
      return [];
    }
  }

  /**
   * Get metadata about the last search operation
   */
  getLastSearchMetadata() {
    return this.lastSearchMetadata || {
      timestamp: null,
      searchedSources: [],
      totalArticles: 0,
      feedsAttempted: 0,
      feedsSuccessful: 0
    };
  }

  /**
   * Fetch from RSS feed with local proxy ONLY
   */
  async fetchFromFeedWithProxy(feedUrl) {
    console.log('🚨 ABC News DEBUG: fetchFromFeedWithProxy called with:', feedUrl);
    console.log('🚨 ABC News DEBUG: Local proxy URL configured as:', this.localProxy);
    console.log('🚨 ABC News DEBUG: Local proxy available:', this.localProxyAvailable);
    
    // RSS Proxy should always be available in production - removing unnecessary availability check
    // if (this.localProxyAvailable === false) {
    //   console.warn('⚠️ ABC News LOCAL PROXY MARKED AS UNAVAILABLE - RETURNING EMPTY RESULTS');
    //   return [];
    // }
    
    // Try local proxy ONLY - NO FALLBACKS TO AVOID CORS SPAM
    try {
      console.log(`🏠 ABC News ATTEMPTING LOCAL PROXY FOR: ${feedUrl}`);
      const proxyUrl = `${this.localProxy}${encodeURIComponent(feedUrl)}`;
      console.log(`🔗 ABC News LOCAL PROXY URL: ${proxyUrl}`);
      
      const response = await Promise.race([
        fetch(proxyUrl, {
          headers: {
            'Accept': 'application/xml, text/xml, application/rss+xml'
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 10000) // 10 second timeout
        )
      ]);

      console.log(`📡 ABC News LOCAL PROXY RESPONSE STATUS: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        throw new Error(`Local proxy failed: HTTP ${response.status} ${response.statusText}`);
      }

      const xmlText = await response.text();
      console.log(`📄 ABC News LOCAL PROXY RETURNED ${xmlText.length} BYTES`);
      
      // More thorough validation of RSS content
      if (!xmlText || xmlText.length < 100) {
        throw new Error('Local proxy returned empty or invalid RSS data');
      }
      
      const articles = this.parseRSSFeed(xmlText, feedUrl);
      console.log(`📰 ABC News PARSED ${articles.length} ARTICLES FROM RSS`);
      
      // Mark proxy as working if we get here successfully
      this.localProxyAvailable = true;
      
      return articles;

    } catch (error) {
      console.error(`❌ ABC News LOCAL PROXY FAILED for ${feedUrl}:`, error.message);
      
      // Mark proxy as potentially failed for future requests
      this.localProxyAvailable = false;
      
      return [];
    }
  }

  /**
   * Build RSS feed URLs based on search options
   */
  buildFeedUrls(options = {}) {
    const feeds = [];
    
    // Always include primary feeds for comprehensive coverage
    feeds.push({
      name: 'ABC Breaking News',
      type: 'breaking',
      priority: 1,
      url: this.endpoints.breaking
    });
    
    feeds.push({
      name: 'ABC Emergency News',
      type: 'emergency', 
      priority: 1,
      url: this.endpoints.emergency
    });
    
    feeds.push({
      name: 'ABC National News',
      type: 'national',
      priority: 2,
      url: this.endpoints.national
    });

    // Add location-specific feeds if location information is available
    if (options.locations && options.locations.length > 0) {
      for (const location of options.locations) {
        const stateFeed = this.getStateSpecificFeed(location);
        if (stateFeed) {
          feeds.push({
            name: `ABC ${stateFeed.state} News`,
            type: 'state_specific',
            priority: 1,
            location: stateFeed.state,
            url: stateFeed.url
          });
        }
      }
    }

    console.log(`📡 ABC News: Built ${feeds.length} feed URLs`);
    return feeds;
  }

  /**
   * Get state-specific feed based on location
   */
  getStateSpecificFeed(location) {
    const locationLower = location.toLowerCase();
    
    // CLEANED UP: Only NSW feeds work, all other state feeds return 500 errors
    // For non-NSW locations, return null to fall back to national feeds
    const stateMapping = {
      'nsw': { state: 'NSW', url: this.endpoints.nsw },
      'new south wales': { state: 'NSW', url: this.endpoints.nsw },
      'sydney': { state: 'NSW', url: this.endpoints.nsw },
      'newcastle': { state: 'NSW', url: this.endpoints.nsw },
      'hunter': { state: 'NSW', url: this.endpoints.nsw },
      'cessnock': { state: 'NSW', url: this.endpoints.nsw }
      // REMOVED: All other state feeds (VIC, QLD, WA, SA, TAS, ACT, NT) - they return 500 errors
      // System will fall back to national feeds for these locations
    };

    return stateMapping[locationLower] || null;
  }

  /**
   * Parse RSS XML into article objects
   */
  parseRSSFeed(xmlText, feedUrl) {
    try {
      // Quick validation - check if it looks like XML
      if (!xmlText || !xmlText.trim().startsWith('<')) {
        console.warn('⚠️ ABC News: Invalid XML response (likely HTML error page)');
        return [];
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Check for parsing errors
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.warn('⚠️ ABC News: XML parsing failed - malformed RSS feed');
        return [];
      }

      const items = xmlDoc.querySelectorAll('item');
      
      if (items.length === 0) {
        console.warn('⚠️ ABC News: No RSS items found in feed');
        return [];
      }

      const articles = [];

      for (const item of items) {
        try {
          const article = this.parseRSSItem(item);
          if (article) {
            articles.push(article);
          }
        } catch (error) {
          // Don't spam console with individual item errors
          continue;
        }
      }

      return articles;

    } catch (error) {
      console.warn('⚠️ ABC News: RSS parsing failed:', error.message);
      return [];
    }
  }

  /**
   * Parse individual RSS item
   */
  parseRSSItem(item) {
    try {
      // Extract basic fields
      const title = this.getTextContent(item, 'title');
      const link = this.getTextContent(item, 'link');
      const description = this.getTextContent(item, 'description');
      const pubDate = this.getTextContent(item, 'pubDate');
      const guid = this.getTextContent(item, 'guid');

      if (!title || !link) {
        return null; // Skip invalid items
      }

      // Parse publication date
      let publishedAt = null;
      if (pubDate) {
        publishedAt = new Date(pubDate);
        if (isNaN(publishedAt.getTime())) {
          publishedAt = new Date(); // Fallback to current time
        }
      } else {
        publishedAt = new Date();
      }

      // Clean up description (remove HTML tags)
      const cleanDescription = this.stripHtml(description);

      // Extract location from content (simple pattern matching)
      const location = this.extractLocation(title, cleanDescription);

      return {
        title: title.trim(),
        url: link.trim(),
        content: cleanDescription?.substring(0, 500) || '', // Limit content length
        source: 'ABC News Australia',
        publishedAt: publishedAt,
        location: location,
        language: 'en',
        country: 'AU',
        sentiment: null, // Will be processed later
        relevanceScore: 0, // Will be calculated later
        keywords: [], // Will be extracted later
        category: 'news',
        guid: guid || link, // Use GUID for deduplication, fallback to link
        rawData: {
          originalDescription: description,
          pubDate: pubDate,
          feedUrl: item.baseURI || 'abc-news-rss'
        }
      };

    } catch (error) {
      console.error('❌ ABC News: Error parsing RSS item:', error);
      return null;
    }
  }

  /**
   * Get text content from XML element
   */
  getTextContent(item, tagName) {
    const element = item.querySelector(tagName);
    return element ? element.textContent.trim() : null;
  }

  /**
   * Strip HTML tags from text
   */
  stripHtml(html) {
    if (!html) return '';
    
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  /**
   * Extract location from article text (Australian-focused pattern matching)
   */
  extractLocation(title, content) {
    const text = `${title} ${content}`.toLowerCase();
    
    // Australian cities and states (prioritized for ABC News)
    const locations = [
      'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'darwin', 'canberra', 'hobart',
      'nsw', 'vic', 'qld', 'wa', 'sa', 'nt', 'act', 'tas',
      'new south wales', 'victoria', 'queensland', 'western australia',
      'south australia', 'northern territory', 'australian capital territory', 'tasmania',
      'gold coast', 'newcastle', 'wollongong', 'geelong', 'townsville', 'cairns',
      'toowoomba', 'ballarat', 'bendigo', 'albury', 'launceston'
    ];

    for (const location of locations) {
      if (text.includes(location)) {
        return location.charAt(0).toUpperCase() + location.slice(1);
      }
    }

    return null;
  }

  /**
   * Add delay for rate limiting
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get provider status
   */
  getStatus() {
    return {
      name: this.name,
      isEnabled: this.isEnabled,
      type: 'rss',
      cost: 'free',
      rateLimit: '2 requests/second',
      coverage: 'Australian news with emergency and breaking news focus',
      endpoints: Object.keys(this.endpoints).length,
      lastSearch: this.lastSearchMetadata
    };
  }

  /**
   * Test connection to ABC News
   */
  async testConnection() {
    try {
      const articles = await this.fetchFromFeedWithProxy(this.endpoints.breaking);
      
      return {
        success: true,
        articlesFound: articles.length,
        message: `Successfully fetched ${articles.length} articles from ABC Breaking News`,
        endpoints: Object.keys(this.endpoints).length
      };

    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to connect to ABC News RSS feeds'
      };
    }
  }
}

export default ABCNewsFeedProvider;